// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import React, { createContext, useRef, useState } from "react";

type CallBackSome<T> = (value: T, index?: number, collection?: T[]) => Promise<boolean>;

export async function some<T>(elements: T[], cb: CallBackSome<T>): Promise<boolean> {
    for (const [index, element] of elements.entries()) {
        if (await cb(element, index, elements)) {
            return true;
        }
    }

    return false;
}

export const StepperContext = createContext<any>({});

const StepperContextProvider: React.FC<any> = ({ initialStep = 1, children }) => {
    const [currentStep, doSetCurrentStep] = useState<number>(initialStep);
    const [maxStep, setMaxStep] = useState<number>(initialStep);
    const validationHookRef = useRef<any[]>([]);

    const setCurrentStep = async (state: number) => {
        const prevent = await some(validationHookRef.current, (item: any) => {
            const hook = item.hook.current(currentStep, state);

            // check is already a promise and return
            if (typeof hook?.then === "function") {
                return hook;
            }
            return Promise.resolve(!!hook);
        });

        if (prevent) {
            return;
        }

        if (currentStep > maxStep) {
            setMaxStep(currentStep);
        }
        doSetCurrentStep(state);
    };

    const preventStepperChange = (hook: any, position = 1) => {
        const vh = validationHookRef.current;
        const index = vh.findIndex((f: any) => f.position === position);
        if (index !== -1) {
            vh.splice(index, 1);
        }
        vh.push({ hook, position });
        vh.sort((a, b) => a.position - b.position);
    };

    return (
        <StepperContext.Provider value={{ currentStep, setCurrentStep, maxStep, setMaxStep, preventStepperChange }}>
            {children}
        </StepperContext.Provider>
    );
};
export default StepperContextProvider;
